import type {TableConfiguration} from 'schema/table/types';
import type {DataFormConfiguration, PreviewConfiguration} from 'schema/types';
import type {Data, Schema} from 'schemaDefinition/types';
import type {CodeListId} from 'services/codelists/types';
import type {ThesaurusId} from 'services/thesaurus/types';

/**
 * A single, flat schema describing a manifestation-like "Book" entity.
 *
 * Part `name`s are chosen to match field keys that already have translations
 * under `form.global.field.*` in `src/localization/locales`, so labels render
 * from the existing i18n instead of showing raw keys. See `getFieldRootKeys` /
 * `useLabelAndPlaceholder` in `src/schema` for how labels are resolved.
 *
 * The same schema instance is reused by all three demo pages (edit, preview,
 * table) to show that one schema definition drives every renderer in
 * `src/schema`. It deliberately uses a flat set of parts (no nested `schema`
 * parts) so it renders cleanly as a table as well as a form/preview.
 *
 * See `src/schemaDefinition/types/parts.ts` for all available part types.
 */
export const bookSchema: Schema = {
  name: 'demoBook',
  parts: [
    {type: 'text', name: 'mainTitle', required: true},
    {type: 'text', name: 'subTitlesValue'},
    {type: 'text', name: 'isbn'},
    {type: 'textarea', name: 'editionNote', maxLength: 2000},
    {type: 'int', name: 'edition'},
    {type: 'int', name: 'pageCount', suffix: ' p.'},
    {type: 'year', name: 'publishedYear'},
    {type: 'date', name: 'inSaleDate'},
    {type: 'codelist', name: 'languages', codelistId: 'LANGUAGE'},
    {type: 'codelist', name: 'country', codelistId: 'COUNTRY'},
    {
      type: 'thesaurus',
      name: 'genreAndForm',
      thesaurusId: 'genreandform',
      cardinality: 'multiple',
    },
    {type: 'bool', name: 'firstEdition'},
  ],
};

/** Codelists referenced by the schema, loaded via `CodeListsGate`. */
export const demoCodeLists: CodeListId[] = ['LANGUAGE', 'COUNTRY'];

/** Thesauruses referenced by the schema, loaded via `ThesaurusesGate`. */
export const demoThesauruses: ThesaurusId[] = ['genreandform'];

/** Sample data for the edit and preview pages. */
export const sampleBook: Data = {
  id: 'book-1',
  mainTitle: 'Søvngjengeren',
  subTitlesValue: 'En roman',
  isbn: '9788203365867',
  editionNote: 'Ny utgave med forord av forfatteren.',
  edition: 2,
  pageCount: 384,
  publishedYear: 2014,
  inSaleDate: '2014-09-01',
  languages: 'nob',
  country: 'NO',
  genreAndForm: ['338', '412'],
  firstEdition: false,
};

/** Sample rows for the table page. */
export const sampleBooks: Data[] = [
  sampleBook,
  {
    id: 'book-2',
    mainTitle: 'The Sleepwalker',
    subTitlesValue: 'A Novel',
    isbn: '9780857058287',
    editionNote: 'English translation.',
    edition: 1,
    pageCount: 352,
    publishedYear: 2018,
    inSaleDate: '2018-03-15',
    languages: 'eng',
    country: 'GB',
    genreAndForm: ['412'],
    firstEdition: true,
  },
  {
    id: 'book-3',
    mainTitle: 'Havboka',
    isbn: '9788249515356',
    edition: 1,
    pageCount: 480,
    publishedYear: 2015,
    inSaleDate: '2015-10-20',
    languages: 'nob',
    country: 'NO',
    genreAndForm: ['338'],
    firstEdition: true,
  },
];

/** Configuration for the edit form. */
export const editConfiguration: Partial<DataFormConfiguration> = {
  showWhenReadonlyAndEmpty: true,
};

/** Configuration for the preview. */
export const previewConfiguration: PreviewConfiguration = {
  layout: {
    showEmptyValues: false,
  },
};

/** Configuration for the table, defining which columns are shown. */
export const tableConfiguration: TableConfiguration = {
  defaultColumns: [
    {name: 'mainTitle', visible: true},
    {name: 'subTitlesValue', visible: true},
    {name: 'isbn', visible: true},
    {name: 'edition', visible: true},
    {name: 'pageCount', visible: true},
    {name: 'publishedYear', visible: true},
    {name: 'inSaleDate', visible: true},
    {name: 'languages', visible: true},
    {name: 'country', visible: true},
    {name: 'genreAndForm', visible: true},
    {name: 'firstEdition', visible: true},
  ],
};
